import { OwnerProvenanceCard } from './OwnerProvenanceCard';
import { ProvenanceListSkeleton } from './OwnerProvenanceCardSkeleton';

import React from 'react';

import { getName, resolvePublicIdentity } from 'utils/parsers';

import { usePagination, useDidMountEffect, useDeferredLoading } from 'hooks';
import { ContentFade } from 'components/UI/ContentFade';
import { useProvenance } from 'hooks/api/useProvenance';
import { CertificateCustomer } from 'types';

import { ProvenanceHistoryItemType } from '@repo/types';

import { Box, Stack, Typography } from '@mui/material';
import { isCertificateOwnedBy } from 'utils/certificateUtils';

interface ProvenanceProps {
  profileId: string | undefined;
  certificate: CertificateCustomer;
  certificateId: string;
  isInPreviewMode: boolean | undefined;
  query?: string;
  ownerIds?: string[];
  dateFrom?: string;
  sortField?: 'owner_name' | 'date' | 'sell_price' | null;
  sortDirection?: 'ASC' | 'DESC' | null;
  additionalSorts?: Array<{ field: 'owner_name' | 'date' | 'sell_price'; direction: 'ASC' | 'DESC' }>;
}

export const Provenance = ({
  profileId,
  certificate,
  certificateId,
  isInPreviewMode,
  query,
  ownerIds = [],
  dateFrom,
  sortField = null,
  sortDirection = null,
  additionalSorts = [],
}: ProvenanceProps) => {
  const fullListItems = 100;

  const { data: provenance, isLoading, refetch } = useProvenance(certificateId, {
    limit: fullListItems,
  });

  /*
   * Measured at 1280 before this: 15 skeleton bars mounted and were gone 220.5ms
   * later, and on a warmer run the same route flashed for a fraction of that. The
   * skeleton is footprint-exact against the loaded list (`OwnerProvenanceCardSkeleton`
   * shares the 8px radius and the `spacing={1.5}` below), so nothing MOVES — what the
   * eye caught was the appearance and disappearance itself. Same symptom the platform
   * hit on its support list, same fix.
   */
  const showSkeleton = useDeferredLoading(isLoading);

  const { page, offset, setPage } = usePagination({
    total: provenance?.total,
    limit: fullListItems,
  });

  const isCertificateOwner = isCertificateOwnedBy(certificate, profileId);

  useDidMountEffect(() => {
    refetch({ offset });
  }, [page]);

  const normalizedQuery = String(query || '')
    .trim()
    .toLowerCase();

  const processedData = (provenance?.data || [])
    .filter(item => {
      const owner = item?.owner as any;

      if (ownerIds.length > 0) {
        // VERA-931 — a store keys off its own label. Falling through to the company
        // branch gave every store the same `company-Unknown` key, so filtering by one
        // shop silently matched all of them.
        const ownerIdToCheck =
          item?.owner_type === ProvenanceHistoryItemType.Customer
            ? owner?.email
            : item?.owner_type === ProvenanceHistoryItemType.Store
              ? `store-${owner?.label || owner?.id || 'Unknown'}`
              : `company-${owner?.name || item?.certificate?.company?.name || 'Unknown'}`;

        if (!ownerIds.includes(ownerIdToCheck)) {
          return false;
        }
      }

      if (dateFrom) {
        const ownershipDate = new Date(item?.ownership_date || '');
        const filterDate = new Date(dateFrom);
        if (ownershipDate < filterDate) {
          return false;
        }
      }

      if (!normalizedQuery) return true;
      const name = (getName(owner) || owner?.name || owner?.label || '').toLowerCase();
      const email = (owner?.email || '').toLowerCase();
      return name.includes(normalizedQuery) || email.includes(normalizedQuery);
    })
    .sort((a, b) => {
      // The synthesized company/mint row is the ROOT of the chain, not a peer of the
      // transfer rows, so it is pinned to the "oldest" end rather than sorted by date.
      // Its date is `certificate.created_at`, which is NOT guaranteed to precede the
      // transfers: any backdated/historical import (e.g. Shopify orders with real past
      // dates) produces transfers older than the certificate row, which would otherwise
      // float the origin of the chain to the top of a newest-first list.
      const aOrigin = a?.owner_type === ProvenanceHistoryItemType.Company;
      const bOrigin = b?.owner_type === ProvenanceHistoryItemType.Company;
      if (aOrigin !== bOrigin) {
        const oldestLast = !sortDirection || sortDirection === 'DESC';
        return aOrigin === oldestLast ? 1 : -1;
      }

      if (!sortField || !sortDirection) {
        const ad = new Date(a?.ownership_date || '').getTime();
        const bd = new Date(b?.ownership_date || '').getTime();
        return bd - ad;
      }

      const allSorts = [{ field: sortField, direction: sortDirection }, ...additionalSorts];

      for (const sort of allSorts) {
        let comparison = 0;

        if (sort.field === 'owner_name') {
          const aOwner = a?.owner as any;
          const bOwner = b?.owner as any;
          const aName = (getName(aOwner) || aOwner?.name || '').toLowerCase();
          const bName = (getName(bOwner) || bOwner?.name || '').toLowerCase();
          comparison = sort.direction === 'ASC' ? aName.localeCompare(bName) : bName.localeCompare(aName);
        } else if (sort.field === 'date') {
          const ad = new Date(a?.ownership_date || '').getTime();
          const bd = new Date(b?.ownership_date || '').getTime();
          comparison = sort.direction === 'ASC' ? ad - bd : bd - ad;
        } else if (sort.field === 'sell_price') {
          const aPrice = Number(a?.price) || 0;
          const bPrice = Number(b?.price) || 0;
          comparison = sort.direction === 'ASC' ? aPrice - bPrice : bPrice - aPrice;
        }

        if (comparison !== 0) {
          return comparison;
        }
      }

      return 0;
    });

  if (showSkeleton) {
    return <ProvenanceListSkeleton />;
  }

  /*
   * Loading, but inside the grace period. NOTHING, not a fall-through to the list —
   * `processedData` is empty while the query is in flight, so the branch below would
   * render "No provenance" over a history that is about to have entries in it.
   * Rendering nothing for <180ms is imperceptible; rendering the wrong answer is not.
   */
  if (isLoading) {
    return null;
  }

  return (
    <ContentFade>
    <Stack spacing={1.5}>
      {processedData.length > 0 ? (
        processedData.map((item, index) => {
          const owner = item?.owner as any;
          const isPerson = item?.owner_type === ProvenanceHistoryItemType.Customer;

          /*
           * WHO THIS ROW IS. Two things were wrong and one of them was a PII leak.
           *
           * The name came from `getName`, which tests `first_name`/`last_name` and
           * returns '' otherwise — so an owner with no name fell through to
           * `owner?.name` (a COMPANY field, absent on a person) and the card rendered
           * its `'—'` placeholder. That is the common case here, not an edge one: a
           * gift recipient frequently has no name, because the point of gifting is
           * sending to someone who may not have finished an account.
           *
           * Under that dash sat `owner.email`, verbatim. A provenance list is on a
           * certificate page, and a certificate page is a LINK YOU SHARE — so the
           * reader is routinely not the person in the row, and this printed a real
           * address to them. `resolvePublicIdentity` is the resolver that stops at the
           * `@handle` for exactly this reason; see its docblock.
           *
           * Company/origin rows keep `owner.name` — a brand's name is the brand, not
           * a person's identity, and `resolvePublicIdentity` has no field for it.
           */
          /*
           * VERA-931 — a STORE row names a PLACE. Its shape carries `label`, `city`
           * and `country`; it has no `name`, so the company branch below rendered
           * the card's `—` placeholder — the same empty-row bug the platform table
           * had. The city becomes the subtext because "Bond Street" alone is
           * ambiguous across a chain that may contain several.
           */
          const isStore = item?.owner_type === ProvenanceHistoryItemType.Store;
          const person = isPerson
            ? resolvePublicIdentity(owner)
            : isStore
              ? {
                  name: owner?.label ?? 'Store',
                  subtext: owner?.city ?? owner?.country ?? null,
                }
              : { name: owner?.name ?? null, subtext: null };

          return (
            <OwnerProvenanceCard
              key={`provenance-${item?.certificate?.id || index}-${String(item?.ownership_date) || index}`}
              name={person.name}
              subtext={person.subtext}
              avatarUrl={owner?.avatar || owner?.logo}
              sinceDate={
                // Raw value on purpose: pre-formatting turns a missing date into today's
                // date (a truthy string), which would defeat the card's empty-date check.
                item?.ownership_date ?? undefined
              }
              /*
               * VERA-924: the company/origin row's date is when the certificate was
               * CREATED, not "owned since" (the brand never "owned" the item at
               * manufacture or certificate-creation time).
               *
               * "Created", not "Certificate created". At 375px the longer label was
               * wide enough to starve the value column, so BOTH cells wrapped to two
               * lines — the label and the timestamp. The date itself is not too wide:
               * the "Owner since" row directly below renders the same format on one
               * line. Shortening the label is therefore the whole fix, and the
               * timestamp survives, which matters because a provenance row is a ledger
               * event whose MOMENT is the point. (The gift state cards dropped their
               * times for a different, measured reason — a 149px date squeezing a name
               * column. Do not harmonise this row with those; harmonise it with the
               * row beneath it.)
               *
               * "Created" is unambiguous here because the brand row above it has
               * already established the subject, and this label is set at this one
               * call site rather than inside the card, so no other surface inherits a
               * word whose subject it has not established.
               */
              /*
               * VERA-931 — "Received" for a store. Neither existing label is true
               * of one: the shop did not become an OWNER (the brand kept ownership
               * throughout), and the certificate was not CREATED there.
               */
              sinceLabel={isStore ? 'Received' : isPerson ? 'Owner since' : 'Created'}
              sellPrice={index === 0 && isCertificateOwner && offset === 0 && item?.price ? `$${item?.price}` : null}
              ownerType={isPerson ? 'customer' : isStore ? 'store' : 'company'}
              acquiredVia={item?.acquired_via ?? null}
              manufacturedDate={item?.manufactured_at ?? null}
              completionMethod={item?.completion_method ?? null}
            />
          );
        })
      ) : (
        <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
          <Typography>No provenance found</Typography>
        </Box>
      )}
    </Stack>
    </ContentFade>
  );
};
